<template>
    <div
        class="content-type-product mt-md-6"
    >
        <ProductJsonLdTag
            v-if="contentData"
            :product="contentData"
        ></ProductJsonLdTag>

        <!-- Main Form -->
        <ContainerContent
            class="mb-8"
            max-class="max-w-md-ceil"
            outer-container
        >
            <!-- Name / Tagline / Rating -->
            <div
                v-if="isMobile"
                class="mb-4"
                v-intersect="{
                    handler: onShowFloatingProductForm,
                    options: {
                        rootMargin: '0px 0px 0px 0px'

                    }
                }"
            >
                <ProductTitle
                    v-if="contentData"
                    :product="contentData"
                    :compact="true"
                    page-title
                ></ProductTitle>
                <ProductRatingTitle
                    :average="contentData.averageRating"
                    :total="contentData.reviewCount"
                    :compact="true"
                ></ProductRatingTitle>
            </div>

            <v-row>
                <!-- Image -->
                <v-col
                    cols="12"
                    md="7"
                    class="
                        d-flex
                        flex-column
                        align-center
                        mb-12
                        mb-md-0
                        px-0
                    "
                >
                    <ProductMainImage
                        v-if="contentData"
                        :product="contentData"
                        :current-sku-uid="currentSkuUid"
                        :image-stack="fullImageStack"
                    ></ProductMainImage>
                </v-col>

                <!-- Form -->
                <v-col
                    cols="12"
                    md="5"
                    class="d-flex flex-column product-form-container-parent"
                >
                    <!-- Name / Tagline / Rating -->
                    <div
                        v-if="!isMobile"
                        class="mb-4"
                        v-intersect="{
                            handler: onShowFloatingProductForm,
                            options: {
                                rootMargin: '0px 0px 0px 0px'

                            }
                        }"
                    >
                        <ProductTitle
                            v-if="contentData"
                            :product="contentData"
                            page-title
                        ></ProductTitle>
                        <ProductRatingTitle
                            :average="contentData.averageRating"
                            :total="contentData.reviewCount"
                            :compact="isMobile"
                        ></ProductRatingTitle>
                    </div>

                    <!-- Product Description -->
                    <div
                        v-if="contentData"
                        class="order-3 order-md-1 mb-6 mb-md-0"
                        v-html="contentData.description"
                    ></div>

                    <v-divider
                        class="mt-10 mb-6 order-2"
                    ></v-divider>

                    <div
                        class="product-form-container order-1 order-md-3"
                    >
                        <!-- Main Form -->
                        <div
                            ref="elProductForm"
                            class="product-form"
                        >
                            <ProductShopForm
                                v-if="skusMap && attrsMapOptions"
                                :page-key="pageKey"
                                :skus-map="skusMap"
                                :product="contentData"
                                :pre-selected-sku-uid="currentSkuUid"
                                :skus="skus"
                                :current-sku-uid="currentSkuUid"
                                :attrs-maps="attrsMapOptions"
                                :option-groups="optionGroups"
                                :spec-groups="specGroups"
                                :show-floating-product-form="showFloatingProductForm"
                                :has-bundle-templates="hasBundleTemplates"
                                @sku-change="onSkuChange($event)"
                                @on-ready="onProductFormReady"
                                @on-image-stack-change="onImageStackChange"
                            ></ProductShopForm>
                        </div>

                        <!-- Product Specs -->
                        <ProductSpecList
                            v-if="skus && specGroups && attrsMapSpecs"
                            class="mt-8"
                            :skus="skus"
                            :current-sku-uid="currentSkuUid"
                            :attrs-maps="attrsMapSpecs"
                            :spec-groups="specGroups"
                        ></ProductSpecList>
                    </div>
                </v-col>
            </v-row>
        </ContainerContent>

        <!-- Features -->
        <section
            v-if="contentData.features"
            class="mb-10"
        >
            <LegacyContentItems
                :items="contentData.features"
            ></LegacyContentItems>
        </section>

        <!-- Warranty & Misc -->
        <ContainerContent
            outer-container
        >
            <section
                v-if="contentData.category?.warrantyPolicy"
            >
                <v-divider
                    v-if="!(contentData.features)"
                    class="my-10"
                ></v-divider>
                <ProductWarrantyInfo
                    :product="contentData"
                ></ProductWarrantyInfo>
            </section>

            <!-- Social / Hashtag -->
            <section
                v-if="contentData.hashtag"
                class="mb-10 mt-6"
            >
                <ProductHashtag
                    :product="contentData"
                ></ProductHashtag>
            </section>
        </ContainerContent>

        <!-- Reviews -->
        <v-lazy
            v-if="contentData"
            ref="elProductReviews"
            v-model="ratingsLazyModel"
            :transition="false"
            :options="{
                rootMargin: '100px 0px 0px 0px'
            }"
        >
            <section
                class="product-section-reviews mb-10 mx-auto max-w-md-ceil"
            >
                <v-divider
                    class="my-10"
                ></v-divider>
                <ProductReviewsPanel
                    :key="`prod-review-panel-${contentData.uid}`"
                    :product-uid="contentData.uid"
                    :sku-uid="currentSkuUid"
                ></ProductReviewsPanel>
            </section>
        </v-lazy>

        <!-- Comparison Groups -->
        <v-lazy>
            <ChildControlledContainer
                tag="section"
                class="product-section-comparison-groups mb-10 mx-auto max-w-md-ceil"
            >
                <v-divider
                    class="my-10"
                ></v-divider>
                <template
                    #child="{ onHide }"
                >
                    <ComparisonGroups
                        :product-uid="contentData.uid"
                        @on-no-data="onHide"
                    ></ComparisonGroups>
                </template>
            </ChildControlledContainer>
        </v-lazy>

        <!-- Featured -->
        <section
            v-if="contentData"
            class="product-section-recommended mb-10 mx-auto max-w-md-ceil"
        >
            <ProductsFeatured
                :product-uid="contentData.uid"
            >
                <template #top="{ hasData }">
                    <v-divider
                        v-if="hasData && !(contentData.features) || (contentData.category?.warrantyPolicy || contentData.hashtag)"
                        class="my-10"
                    ></v-divider>
                </template>
            </ProductsFeatured>
        </section>
    </div>
</template>

<script lang="ts" setup>
    import ProductMainImage from '~/components/products/ProductMainImage/ProductMainImage.vue';
    import ProductTitle from '~/components/products/product/ProductTitle/ProductTitle.vue';
    import ProductRatingTitle from '~/components/products/ProductRatingTitle/ProductRatingTitle.vue';
    import ProductShopForm from '~/components/products/ProductShopForm/ProductShopForm.vue';
    import ContainerContent from '~/components/ContainerContent/ContainerContent.vue';
    import ProductJsonLdTag from '~/components/products/ProductJsonLdTag/ProductJsonLdTag.server.vue';
    import ComparisonGroups from '~/components/products/product/comparison-groups/ComparisonGroups/ComparisonGroups.vue';
    import ChildControlledContainer from '~/components/ChildControlledContainer/ChildControlledContainer.vue';
    import type {
        ProductAttrsMaps,
        ContentData,
        ContentMeta,
        Product,
        Sku,
        SkusMap,
        FullImageStack
    } from '~/types';
    import {
        useIsMobile,
        useUiState,
        useFullImage,
        useSkusUtils,
        useProductAttrs,
        useDebug
    } from '~/composables';
    import { getSingleParam } from 'x1-base/utils';
    import { useSeoMeta } from '#imports';
    import type { VLazy } from 'vuetify/components';

    const LegacyContentItems = defineAsyncComponent(() => import('~/components/content/LegacyContentItems/LegacyContentItems.server.vue'));
    const ProductWarrantyInfo = defineAsyncComponent(() => import('~/components/products/ProductWarrantyInfo/ProductWarrantyInfo.vue'));
    const ProductHashtag = defineAsyncComponent(() => import('~/components/products/ProductHashtag/ProductHashtag.vue'));
    const ProductsFeatured = defineAsyncComponent(() => import('~/components/products/ProductsFeatured/ProductsFeatured.vue'));
    const ProductReviewsPanel = defineAsyncComponent(() => import('~/components/products/reviews/ProductReviewsPanel/ProductReviewsPanel.vue'));
    const ProductSpecList = defineAsyncComponent(() => import('~/components/products/ProductSpecList/ProductSpecList.vue'));

    const props = defineProps<{
        contentMeta: ContentMeta
        contentData: Extract<ContentData, Product>
        pageKey: string;
    }>();

    const cssPadVar = '--app-bottom-padding';
    const { isMobile } = useIsMobile();

    const {
        processSkus,
        getSkuBySlug
    } = useSkusUtils();

    const {
        processAttrsGroups
    } = useProductAttrs();

    const {
        skus,
        optionGroups,
        specGroups
    } = props.contentData;

    const debugConsole = useDebug();

    const skusMap = useState<Nilish<SkusMap>>('keyTypeSkusMap', () => null);
    const attrsMapSpecs = useState<Nilish<ProductAttrsMaps>>('keyTypeProdAttrsMapSpecs', () => null);
    const attrsMapOptions = useState<Nilish<ProductAttrsMaps>>('keyTypeProdAttrsMapOpts', () => null);
    const hasBundleTemplates = useState<boolean>('keyTypeProdHasBundleTmpl', () => false);
    const fullImageStack = useState<Nilish<FullImageStack>>('keyTypeProdFullImageStack', () => null);

    callOnce(() => {
        const processedSkus = processSkus(skus);
        skusMap.value = processedSkus.skusMap;
        hasBundleTemplates.value = processedSkus.hasBundleTemplates;
        attrsMapSpecs.value = processAttrsGroups(specGroups);
        attrsMapOptions.value = processAttrsGroups(optionGroups);
    }, { mode: 'navigation' });

    const route = useRoute();
    const { setRootCssVar } = useUiState();
    const currentSkuUid = useState<Nullable<string>>('keyTypeProdCurrentSkuUid', () => props.contentData?.defaultSkuUid ?? null);
    const isProductFormReady = ref(false);
    const showFloatingProductForm = ref(false);
    const elProductForm = shallowRef<HTMLElement>();
    const ratingsLazyModel = ref<boolean>(false);
    const elProductReviews = shallowRef<InstanceType<typeof VLazy>>();

    function onSkuChange(skuUid: Nullable<string>): void {
        currentSkuUid.value = skuUid;
    }

    function onProductFormReady(): void {
        isProductFormReady.value = true;
    }

    function setSkuMetaTags(sku: Nilish<Sku>): void {
        if (import.meta.server && sku) {
            const img = sku.images?.[0];

            if (!img) {
                return;
            }

            const ogImage = useFullImage().getSingleImageUrlFromFullImage(img, 'x-large') ?? '';
            const description = sku.seoDescription ?? sku.detail;

            useSeoMeta({
                ogImage,
                ogImageAlt: img.alt,
                ogDescription: description,
                twitterDescription: description
            });
        }
    }

    function setPreSelectedSkuUid(): void {
        const preSelectedSku = getSingleParam(route.query, 'sku');

        if (preSelectedSku) {
            const sku = getSkuBySlug(skus, preSelectedSku);
            setSkuMetaTags(sku);
            currentSkuUid.value = sku?.uid ?? null;
        } else if (props.contentData) {
            currentSkuUid.value = props.contentData.defaultSkuUid;
        }
    }

    function onShowFloatingProductForm(isIntersect: boolean, [entity]: [IntersectionObserverEntry]): void {
        showFloatingProductForm.value = entity.target.getBoundingClientRect().y < 0 && !isIntersect;
    }

    function onImageStackChange(imgStack: Nullable<FullImageStack>): void {
        debugConsole.log('IMAGE STACK: ', imgStack);
        fullImageStack.value = imgStack;
    }

    onServerPrefetch(() => {
        setPreSelectedSkuUid();
    });

    onBeforeMount(() => {
        setPreSelectedSkuUid();
        setRootCssVar(cssPadVar, '80px');
    });

    onBeforeUnmount(() => {
        setRootCssVar(cssPadVar, '0');
    });
</script>

<style lang="scss">
.product-form-container-parent {
    position: relative;
    transition: all 0.1s ease;
    overflow: unset;

}

.layout-desktop {
    .product-form-container {
        transition: all 0.1s ease;
        position: sticky;
        // NOTE: References a header height
        top: 144px;
    }
}
</style>
